import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/FloorLayoutEditor.css';

interface AssignLockerModalProps {
  isOpen: boolean;
  workspaceId: string;
  lockerSpace: any;
  onClose: () => void;
  onSuccess: () => void;
}

interface Unit {
  _id: string;
  unitNo: string;
  unitNoLegal?: string;
  projectId: string;
  projectName?: string;
  dealId?: string;
  floorMarketing?: number;
}

interface EligibilityResponse {
  eligible: boolean;
  message: string;
  maxAllowed: number;
  currentCount: number;
  hasDeal: boolean;
}

const AssignLockerModal: React.FC<AssignLockerModalProps> = ({
  isOpen,
  workspaceId,
  lockerSpace,
  onClose,
  onSuccess
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Unit[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [eligibility, setEligibility] = useState<EligibilityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setSearchQuery('');
      setSearchResults([]);
      setSelectedUnit(null);
      setEligibility(null);
      setShowDropdown(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const searchUnits = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(`/workspaces/${workspaceId}/units/search`, {
          params: {
            query: searchQuery,
            projectId: lockerSpace.projectId,
            limit: 10
          }
        });
        setSearchResults(response.data.units || []);
        setShowDropdown(true);
      } catch (error) {
        console.error('Error searching units:', error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchUnits, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, workspaceId, lockerSpace.projectId]);

  const handleSelectUnit = async (unit: Unit) => {
    setSelectedUnit(unit);
    setSearchQuery(unit.unitNo);
    setShowDropdown(false);
    setEligibility(null);

    // Check eligibility
    try {
      setLoading(true);
      const response = await api.get(
        `/workspaces/${workspaceId}/units/${unit._id}/locker-eligibility`,
        {
          params: { spaceId: lockerSpace._id }
        }
      );
      setEligibility(response.data);
    } catch (error: any) {
      console.error('Error checking eligibility:', error);
      toast.error(error.response?.data?.message || 'Failed to check eligibility');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedUnit || !eligibility?.eligible) {
      return;
    }

    try {
      setSubmitting(true);
      await api.post(`/workspaces/${workspaceId}/locker-spaces/assign`, {
        spaceId: lockerSpace._id,
        unitId: selectedUnit._id
      });
      toast.success('Locker space assigned successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error assigning locker space:', error);
      toast.error(error.response?.data?.message || 'Failed to assign locker space');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const addons = lockerSpace.addons || [];
  const mandatoryAddons = lockerSpace.mandatoryAddons || [];

  return (
    <div className="floor-layout-modal-overlay" onClick={onClose}>
      <div className="floor-layout-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div className="floor-layout-modal-header">
          <h3>Assign Locker Space</h3>
          <button className="floor-layout-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="floor-layout-modal-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {/* Locker Space Details */}
          <div style={{ marginBottom: '24px', padding: '16px', background: '#F9FAFB', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
              Locker Space Details
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
              <div>
                <span style={{ color: '#6B7280' }}>Locker Number:</span>
                <span style={{ marginLeft: '8px', fontWeight: '500', color: '#111827' }}>
                  {lockerSpace.number}
                </span>
              </div>
              <div>
                <span style={{ color: '#6B7280' }}>Floor:</span>
                <span style={{ marginLeft: '8px', fontWeight: '500', color: '#111827' }}>
                  {lockerSpace.floorName}
                </span>
              </div>
              <div>
                <span style={{ color: '#6B7280' }}>Type:</span>
                <span style={{ marginLeft: '8px', fontWeight: '500', color: '#111827' }}>
                  {lockerSpace.type}
                </span>
              </div>
              <div>
                <span style={{ color: '#6B7280' }}>Status:</span>
                <span style={{ marginLeft: '8px', fontWeight: '500', color: '#111827' }}>
                  {lockerSpace.status}
                </span>
              </div>
            </div>

            {addons.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Add-ons:</span>
                <span style={{ marginLeft: '8px', fontWeight: '500', color: '#111827', fontSize: '14px' }}>
                  {addons.map((a: any) => a.name).join(', ')}
                </span>
              </div>
            )}

            {mandatoryAddons.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Mandatory Add-ons:</span>
                <span style={{ marginLeft: '8px', fontWeight: '500', color: '#111827', fontSize: '14px' }}>
                  {mandatoryAddons.map((a: any) => a.name).join(', ')}
                </span>
              </div>
            )}
          </div>

          {/* Unit Search */}
          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="unit-search" style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
              Search Unit No
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="unit-search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Type unit number to search..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                autoComplete="off"
              />

              {/* Dropdown */}
              {showDropdown && searchResults.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    background: 'white',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000
                  }}
                >
                  {searchResults.map((unit) => (
                    <div
                      key={unit._id}
                      onClick={() => handleSelectUnit(unit)}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #F3F4F6',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F9FAFB';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      <div style={{ fontWeight: '500', color: '#111827' }}>
                        Unit {unit.unitNo}
                        {unit.unitNoLegal && unit.unitNoLegal !== unit.unitNo && (
                          <span style={{ color: '#6B7280', fontWeight: 'normal' }}> ({unit.unitNoLegal})</span>
                        )}
                      </div>
                      {unit.floorMarketing && (
                        <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                          Floor {unit.floorMarketing}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {loading && (
                <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                  <div className="builders-loading-spinner" style={{ width: '16px', height: '16px' }} />
                </div>
              )}
            </div>
          </div>

          {/* Eligibility Message */}
          {eligibility && (
            <div
              style={{
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '14px',
                background: eligibility.eligible ? '#ECFDF5' : '#FEF2F2',
                border: `1px solid ${eligibility.eligible ? '#10B981' : '#EF4444'}`,
                color: eligibility.eligible ? '#065F46' : '#991B1B'
              }}
            >
              {eligibility.message}
            </div>
          )}
        </div>

        <div className="floor-layout-modal-footer" style={{ flexShrink: 0 }}>
          <button onClick={onClose} className="btn btn-secondary btn-md" disabled={submitting}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-primary btn-md"
            disabled={!selectedUnit || !eligibility?.eligible || submitting}
          >
            {submitting ? 'Assigning...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignLockerModal;
